import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { UserService } from '../../core/services/user.service';
import { StudioService } from '../../core/services/studio.service';
import { OwnerService } from '../../core/services/owner.service';
import { KeyNumberService } from '../../core/services/key-number.service';
import { FlashService } from '../../core/services/flash.service';

@Component({
    selector: 'app-staff-new',
    imports: [RouterLink, FormsModule],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-lg-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;新規作成</span>
        </div>
        <div class="card-body">
          <div style="height: 1px"></div>
          <form novalidate name="staffNewForm" #staffNewForm="ngForm" (ngSubmit)="save(staffNewForm)">
            <div class="row">
              <div class="col-md-12">

                <!-- 所属 -->
                <div class="form-group row">
                  <div class="col-md-4">
                    <label class="col-form-label">所属</label>
                    <select class="form-control" [(ngModel)]="selectedOwner" name="owner"
                            (ngModelChange)="onOwnerChange()" required>
                      <option [ngValue]="null">選択してください</option>
                      @for (owner of owners; track owner.id) {
                        <option [ngValue]="owner">{{ owner.name }}</option>
                      }
                    </select>
                  </div>
                </div>

                <!-- 店舗（multi-select） -->
                <div class="row">
                  <div class="col-md-4">
                    <label class="col-form-label">店舗</label>
                  </div>
                </div>
                <div class="form-group col-md-6 border" style="max-height:300px;overflow-y:auto">
                  <table class="table table-sm mt-2 mb-2">
                    @for (studio of filteredStudios; track studio.id) {
                      <tr>
                        <td class="border-0">
                          <div class="custom-control custom-checkbox custom-control-inline w-100">
                            <input class="custom-control-input"
                                   [id]="'check_' + studio.id"
                                   type="checkbox"
                                   [(ngModel)]="studioCheck[studio.id]"
                                   (ngModelChange)="updateFilteredKeyItems()"
                                   [name]="'studio_' + studio.id">
                            <label class="custom-control-label w-100" [for]="'check_' + studio.id">
                              {{ studio.name }}
                            </label>
                          </div>
                        </td>
                      </tr>
                    }
                  </table>
                </div>

                <!-- 従業員名 -->
                <div class="form-group row">
                  <div class="col-md-8">
                    <label class="col-form-label">従業員名</label>
                    <input type="text" class="form-control" [(ngModel)]="form.name" name="name" required>
                  </div>
                </div>

                <!-- パスワード -->
                <div class="form-group row">
                  <div class="col-md-8">
                    <label class="col-form-label">パスワード</label>
                    <div class="input-group">
                      <input [type]="showPassword ? 'text' : 'password'" class="form-control" [(ngModel)]="form.password" name="password"
                             autocomplete="new-password" required [pattern]="passwordPattern"
                             placeholder="半角英字、数字、記号で入力してください">
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" (click)="showPassword = !showPassword">
                          <span [class]="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- メールアドレス -->
                <div class="form-group row">
                  <div class="col-md-8">
                    <label class="col-form-label">メールアドレス</label>
                    <input type="email" class="form-control" [(ngModel)]="form.email" name="email" required email>
                  </div>
                </div>

                <!-- 入退館キー -->
                <div class="form-group row">
                  <div class="col-md-4">
                    <label class="col-form-label">入退館キー</label>
                    <select class="form-control" [(ngModel)]="selectedKey" name="keyNumber">
                      <option [ngValue]="null">選択してください</option>
                      @for (item of filteredKeyItems; track item.id) {
                        <option [ngValue]="item">{{ item.keyNumber }}</option>
                      }
                    </select>
                  </div>
                </div>

                <!-- ボタン -->
                <div class="form-group row">
                  <div class="col-md-12 text-right">
                    <a routerLink="/staff" class="btn bg-close text-white">
                      <i class="fa s-icon-cancel"></i>&nbsp;閉じる
                    </a>
                    <button type="submit" class="btn btn-success ml-1"
                            [disabled]="saving || staffNewForm.invalid || !hasCheckedStudio()">
                      <i class="fa s-icon-save"></i>&nbsp;保存
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class StaffNewComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private userService = inject(UserService);
  private studioService = inject(StudioService);
  private ownerService = inject(OwnerService);
  private keyNumberService = inject(KeyNumberService);
  private router = inject(Router);
  private flash = inject(FlashService);

  form: any = { role: 30 };
  showPassword = false;
  owners: any[] = [];
  allStudios: any[] = [];
  filteredStudios: any[] = [];
  studioCheck: Record<number, boolean> = {};
  keyItems: any[] = [];
  filteredKeyItems: any[] = [];
  selectedOwner: any = null;
  selectedKey: any = null;
  saving = false;
  readonly passwordPattern = '^[!-~]+$';

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('店舗管理', 's-icon-studio');
    const [owners, studios] = await Promise.all([
      this.ownerService.list('all') as Promise<any[]>,
      this.studioService.list('all') as Promise<any[]>,
    ]);
    this.owners = owners ?? [];
    this.allStudios = studios ?? [];
    this.filteredStudios = [...this.allStudios];
    if (this.owners.length === 1) {
      this.selectedOwner = this.owners[0];
      this.onOwnerChange();
    }
  }

  async onOwnerChange(): Promise<void> {
    this.studioCheck = {};
    this.keyItems = [];
    this.filteredKeyItems = [];
    this.selectedKey = null;
    if (!this.selectedOwner) {
      this.filteredStudios = [...this.allStudios];
      return;
    }
    this.filteredStudios = this.allStudios.filter((s: any) => s.ownerId === this.selectedOwner.id);
    const studioIds = this.filteredStudios.map((s: any) => s.id);
    const keyResults = await Promise.all(
      studioIds.map((id: number) => this.studioService.getKeyNumber(id) as Promise<any[]>)
    );
    this.keyItems = keyResults.flat().filter(Boolean);
    this.updateFilteredKeyItems();
  }

  updateFilteredKeyItems(): void {
    const checkedIds = this.getCheckedStudioIds();
    this.filteredKeyItems = this.keyItems.filter((v: any) => {
      const inChecked = checkedIds.includes(v.studioId);
      const available = v.user === null || (v.user && v.id !== v.user.keyNumberId);
      return inChecked && available && v.isStaff;
    });
  }

  hasCheckedStudio(): boolean {
    return Object.values(this.studioCheck).some(Boolean);
  }

  getCheckedStudioIds(): number[] {
    return Object.entries(this.studioCheck)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id));
  }

  async save(form?: NgForm): Promise<void> {
    if (form?.invalid || !this.form.name || !this.form.password || !this.form.email || !this.selectedOwner || !this.hasCheckedStudio()) {
      form?.control.markAllAsTouched();
      return;
    }
    this.saving = true;
    try {
      const payload = { ...this.form, ownerId: this.selectedOwner.id };
      const saved: any = await this.userService.upsert(payload);
      const userId = saved?.id ?? saved?.result?.id;
      if (userId) {
        const studioEntries = this.getCheckedStudioIds().map(studioId => ({ userId, studioId }));
        await this.userService.createUsersStudios(studioEntries);
        // 旧版 staff.js:192-204: 成功 Flash、キー更新失敗は danger Flash（表示は遷移先 staff 一覧の <flash-message>）
        this.flash.create('success', '従業員データを追加しました');
        if (this.selectedKey) {
          const keyResult = await this.keyNumberService.update({ ...this.selectedKey, userId });
          if (!keyResult || keyResult === 'No target') {
            this.flash.create('danger', '入退館キーの追加に失敗しました。従業員編集から改めて設定してください');
          }
        }
      }
      this.router.navigate(['/staff']);
    } finally {
      this.saving = false;
    }
  }
}
